import { Portal } from '@headlessui/react';
import { ChevronDownIcon, PlusIcon, TagIcon } from '@heroicons/react/outline';
import classNames from 'classnames';
import { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';

import {
  ProgramTagDataFragment,
  useCreateProgramTagMutation,
  useMeProviderProgramTagsLazyQuery,
} from '../../../generated/graphql';

import { getTagStyles, DEFAULT_TAG_COLOR } from '../../lib/colors';

type ProgramTagSelectDropdownProps = {
  programTags: ProgramTagDataFragment[];
  value: string;
  onChange: (tagId: string) => void;
  /** When provided, called with the full tag on select/create. Use for multi-select. */
  onTagSelected?: (tag: ProgramTagDataFragment) => void;
  onCreateTag?: (name: string) => Promise<ProgramTagDataFragment | null>;
  id?: string;
  label?: string;
  placeholder?: string;
  excludeTagIds?: string[];
  onError?: (message: string) => void;
};

const ProgramTagSelectDropdown: FC<ProgramTagSelectDropdownProps> = ({
  programTags,
  value,
  onChange,
  onTagSelected,
  onCreateTag,
  id = 'program-tag-select',
  label = 'Which tag?',
  placeholder = 'Search or create tag',
  excludeTagIds = [],
  onError,
}) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [, { refetch: refetchProgramTags }] =
    useMeProviderProgramTagsLazyQuery();
  const [createProgramTagMutation] = useCreateProgramTagMutation();
  const excludeSet = new Set(excludeTagIds);
  const primaryId = (value || '').trim();
  const selectedTag = primaryId
    ? programTags.find((t) => t.id === primaryId)
    : null;
  const availableTags = programTags.filter(
    (t) => !excludeSet.has(t.id) || t.id === primaryId,
  );
  const filteredTags = query.trim()
    ? availableTags.filter((t) =>
        t.name.toLowerCase().includes(query.toLowerCase()),
      )
    : availableTags;
  const exactMatch = query.trim()
    ? availableTags.find(
        (t) => t.name.toLowerCase() === query.trim().toLowerCase(),
      )
    : null;
  const showCreateOption = query.trim().length > 0 && !exactMatch;

  useLayoutEffect(() => {
    if (open && buttonRef.current) {
      setDropdownRect(buttonRef.current.getBoundingClientRect());
    } else {
      setDropdownRect(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updateRect = () => {
      if (buttonRef.current) {
        setDropdownRect(buttonRef.current.getBoundingClientRect());
      }
    };
    window.addEventListener('scroll', updateRect, true);
    window.addEventListener('resize', updateRect);
    return () => {
      window.removeEventListener('scroll', updateRect, true);
      window.removeEventListener('resize', updateRect);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        buttonRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSelectTag = (tagIdToSelect: string) => {
    const tag = programTags.find((t) => t.id === tagIdToSelect);
    onChange(tagIdToSelect);
    if (tag) onTagSelected?.(tag);
    setQuery('');
    setOpen(false);
  };

  const handleCreateTag = async () => {
    const tagName = query.trim();
    if (!tagName) return;
    if (onCreateTag) {
      const created = await onCreateTag(tagName);
      if (created?.id) {
        onChange(created.id);
        onTagSelected?.(created);
        setQuery('');
        setOpen(false);
      } else {
        onError?.('Could not create tag. Please try again.');
      }
      return;
    }
    try {
      const { data } = await createProgramTagMutation({
        variables: {
          input: { name: tagName, color: DEFAULT_TAG_COLOR },
        },
      });
      const created = data?.createProgramTag;
      if (created?.id) {
        const tag: ProgramTagDataFragment = {
          __typename: 'ProgramTag',
          id: created.id,
          name: created.name,
          description: created.description,
          color: created.color,
        };
        onChange(created.id);
        onTagSelected?.(tag);
        setQuery('');
        setOpen(false);
        await refetchProgramTags();
      }
    } catch {
      onError?.('Could not create tag. Please try again.');
    }
  };

  const renderTagOval = (tag: ProgramTagDataFragment) => {
    const styles = getTagStyles(tag.color ?? DEFAULT_TAG_COLOR);
    return (
      <div
        className="flex items-center gap-2 rounded-full px-2 py-1"
        style={styles}
      >
        <span className="text-sm">{tag.name}</span>
      </div>
    );
  };

  const dropdownContent = open && dropdownRect && (
    <Portal>
      <div
        ref={dropdownRef}
        className="overflow-hidden rounded-md border border-neutral-100 bg-white shadow-lg"
        style={{
          position: 'fixed',
          top: dropdownRect.bottom + 4,
          left: dropdownRect.left,
          width: dropdownRect.width,
          zIndex: 10000,
        }}
      >
        <div className="border-b border-neutral-100 bg-white px-2 py-2">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tags..."
            className="text-sm w-full rounded border border-neutral-100 px-3 py-2 placeholder:text-neutral-110 focus:border-primary-125 focus:outline-none focus:ring-1 focus:ring-primary-125"
          />
        </div>
        <div className="max-h-48 overflow-auto py-1">
          {filteredTags.length === 0 && !showCreateOption ? (
            <div className="text-sm text-neutral-130 px-3 py-2">
              {query.trim() ? 'No tags found' : 'Type to search or create'}
            </div>
          ) : (
            <>
              {showCreateOption && (
                <button
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateTag();
                  }}
                  className="flex w-full cursor-pointer items-center gap-2 px-3 py-2 font-normal text-secondary-125 hover:bg-primary-25"
                >
                  <PlusIcon className="h-5 w-5 shrink-0" aria-hidden />
                  <span>Create &quot;{query.trim()}&quot;</span>
                </button>
              )}
              {filteredTags.map((tag) => (
                <button
                  key={tag.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleSelectTag(tag.id);
                  }}
                  className={classNames(
                    'flex w-full cursor-pointer items-center gap-2 px-3 py-2 font-normal text-neutral-150 hover:bg-primary-25',
                    primaryId === tag.id && 'bg-primary-25',
                  )}
                >
                  {renderTagOval(tag)}
                </button>
              ))}
            </>
          )}
        </div>
      </div>
    </Portal>
  );

  return (
    <div className="relative space-y-1">
      {label && (
        <label
          className="text-xs text-neutral-130 block font-semibold"
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <button
        ref={buttonRef}
        type="button"
        id={id}
        aria-expanded={open}
        aria-haspopup="listbox"
        onMouseDown={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen((o) => !o);
          }
        }}
        className={classNames(
          'flex w-full items-center gap-2 rounded-md border bg-white py-2 pl-3 pr-2 shadow-sm',
          'border-neutral-100',
        )}
      >
        {selectedTag ? (
          renderTagOval(selectedTag)
        ) : (
          <>
            <TagIcon
              className="h-5 w-5 shrink-0 text-secondary-125"
              aria-hidden
            />
            <span className="text-sm min-w-0 flex-1 truncate text-left font-normal text-neutral-150">
              {placeholder}
            </span>
          </>
        )}
        <ChevronDownIcon
          className="h-5 w-5 shrink-0 text-neutral-110"
          aria-hidden
        />
      </button>
      {dropdownContent}
    </div>
  );
};

export default ProgramTagSelectDropdown;
